import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

const TABS = [
  { key: "general", label: "General" },
  { key: "hero", label: "Hero" },
  { key: "story", label: "Story / Vision / Mission" },
  { key: "stats", label: "Statistics" },
  { key: "featured", label: "Featured Courses" },
  { key: "partners", label: "Hiring Partners" },
  { key: "tools", label: "Tools Stack" },
  { key: "founder", label: "Founder & Video" },
  { key: "cta", label: "CTA" },
  { key: "seo", label: "SEO" },
];

export default function AdminAbout() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("general");
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data));
    api.get("/courses").then(({ data }) => setCourses(data));
  }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const set = (path, value) => {
    const clone = JSON.parse(JSON.stringify(data));
    const parts = path.split(".");
    let cur = clone;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setData(clone);
  };
  const save = async () => {
    try { await api.put("/settings", { data }); toast.success("About page saved"); refresh(); }
    catch { toast.error("Save failed"); }
  };
  const F = ({ label, path, type = "text", rows }) => {
    const parts = path.split("."); let val = data;
    for (const p of parts) val = val?.[p] ?? "";
    const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
    if (type === "textarea") return (
      <div><label className="text-xs font-semibold text-neutral-600">{label}</label>
        <textarea rows={rows || 3} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`about-${path}`} /></div>);
    if (type === "checkbox") return (
      <label className="flex items-center gap-2"><input type="checkbox" checked={!!val} onChange={(e) => set(path, e.target.checked)} data-testid={`about-${path}`} /><span className="text-sm">{label}</span></label>);
    return (
      <div><label className="text-xs font-semibold text-neutral-600">{label}</label>
        <input type={type} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`about-${path}`} /></div>);
  };

  const ap = data.about_page || {};
  const stats = ap.achievements || [];
  const partners = data.hiring_partners || [];
  const featSlugs = ap.featured_courses_slugs || [];
  const tools = ["tools_ai", "tools_ads", "tools_seo"];

  // Statistics helpers
  const addStat = () => set("about_page.achievements", [...stats, { metric: "", label: "" }]);
  const rmStat = (i) => set("about_page.achievements", stats.filter((_, x) => x !== i));
  const moveStat = (i, dir) => {
    const arr = [...stats]; const j = i + dir; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; set("about_page.achievements", arr);
  };
  const setStat = (i, k, v) => set("about_page.achievements", stats.map((s, x) => x === i ? { ...s, [k]: v } : s));

  // Partner helpers
  const addPartner = () => set("hiring_partners", [...partners, { name: "", logo: "", website: "", featured: false, visible: true }]);
  const rmPartner = (i) => set("hiring_partners", partners.filter((_, x) => x !== i));
  const setPartner = (i, k, v) => set("hiring_partners", partners.map((p, x) => x === i ? { ...p, [k]: v } : p));

  // Featured-course toggle
  const toggleFeat = (slug) => {
    const next = featSlugs.includes(slug) ? featSlugs.filter((s) => s !== slug) : [...featSlugs, slug];
    set("about_page.featured_courses_slugs", next);
  };

  return (
    <div className="p-8 pb-24 max-w-6xl" data-testid="admin-about-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl" data-testid="admin-about-title">About Us Page</h1>
          <p className="text-neutral-500 mt-1">Every section, statistic, course, partner and CTA is edited here — no code required.</p>
        </div>
        <div className="flex gap-2">
          <a href="/about" target="_blank" rel="noreferrer" className="btn-outline-eq text-sm">Preview →</a>
          <button onClick={save} className="btn-primary-eq text-sm" data-testid="about-save"><Save className="w-4 h-4" /> Save & Publish</button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-100"}`}
            data-testid={`about-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-neutral-200">
        {tab === "general" && (<div className="grid gap-4">
          <F label="Introduction heading" path="about_page.intro_heading" />
          <F label="Introduction body" path="about_page.intro_body" type="textarea" rows={4} />
          <F label="Ecosystem heading" path="about_page.ecosystem_heading" />
          <F label="Ecosystem body" path="about_page.ecosystem_body" type="textarea" />
          <F label="Certifications heading" path="about_page.certifications_heading" />
          <F label="Certifications body" path="about_page.certifications_body" type="textarea" />
        </div>)}

        {tab === "hero" && (<div className="grid gap-4">
          <F label="Banner subtitle (kicker)" path="about_page.banner_subtitle" />
          <F label="Banner title" path="about_page.banner_title" type="textarea" rows={2} />
          <F label="Banner image URL" path="about_page.banner_image" />
          {ap.banner_image && <img src={ap.banner_image} alt="" className="w-full max-h-56 object-cover rounded-xl mt-2" />}
        </div>)}

        {tab === "story" && (<div className="grid gap-4">
          <F label="Story heading" path="about_page.story_heading" />
          <F label="Story body" path="about_page.story_body" type="textarea" rows={4} />
          <F label="Vision heading" path="about_page.vision_heading" />
          <F label="Vision body" path="about_page.vision_body" type="textarea" />
          <F label="Mission heading" path="about_page.mission_heading" />
          <F label="Mission body" path="about_page.mission_body" type="textarea" />
        </div>)}

        {tab === "stats" && (<div>
          <p className="text-sm text-neutral-500 mb-4">Statistics render as elevated 3D cards on the About page. Numbers with "+", "%" or "/" animate on scroll.</p>
          <div className="space-y-3">
            {stats.map((s, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-xl border border-neutral-200" data-testid={`stat-row-${i}`}>
                <input value={s.metric || ""} onChange={(e) => setStat(i, "metric", e.target.value)}
                       placeholder="500+" className="w-32 px-3 py-2 border border-neutral-200 rounded-lg text-sm font-bold" data-testid={`stat-metric-${i}`} />
                <input value={s.label || ""} onChange={(e) => setStat(i, "label", e.target.value)}
                       placeholder="Students Trained" className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`stat-label-${i}`} />
                <button onClick={() => moveStat(i, -1)} disabled={i === 0} className="p-1.5 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => moveStat(i, 1)} disabled={i === stats.length - 1} className="p-1.5 hover:bg-neutral-100 rounded disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => rmStat(i)} className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`stat-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={addStat} className="mt-4 btn-outline-eq text-sm" data-testid="stat-add"><Plus className="w-4 h-4" /> Add statistic</button>
        </div>)}

        {tab === "featured" && (<div>
          <F label="Section heading" path="about_page.featured_courses_heading" />
          <F label="Enable featured courses section" path="about_page.featured_courses_enabled" type="checkbox" />
          <div className="mt-4">
            <label className="text-xs font-semibold text-neutral-600">Layout</label>
            <select value={ap.featured_courses_layout || "grid"} onChange={(e) => set("about_page.featured_courses_layout", e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm">
              <option value="grid">Grid (3 columns)</option>
              <option value="carousel">Carousel (horizontal scroll)</option>
            </select>
          </div>
          <div className="mt-4 space-y-2">
            <F label="Show price" path="about_page.featured_courses_show_price" type="checkbox" />
            <F label="Show duration" path="about_page.featured_courses_show_duration" type="checkbox" />
            <F label="Show trainer" path="about_page.featured_courses_show_trainer" type="checkbox" />
            <F label="Show syllabus button" path="about_page.featured_courses_show_syllabus" type="checkbox" />
            <F label="Show Enrol Now button" path="about_page.featured_courses_show_enroll" type="checkbox" />
          </div>
          <div className="mt-5">
            <div className="text-xs font-semibold text-neutral-600 mb-2">Select courses to feature (pulled live from Course Management)</div>
            <div className="grid md:grid-cols-2 gap-2">
              {courses.map((c) => (
                <label key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${featSlugs.includes(c.slug) ? "border-sky-500 bg-sky-50" : "border-neutral-200"}`} data-testid={`feat-course-${c.slug}`}>
                  <input type="checkbox" checked={featSlugs.includes(c.slug)} onChange={() => toggleFeat(c.slug)} />
                  {c.thumbnail && <img src={c.thumbnail} alt="" className="w-12 h-12 rounded object-cover" />}
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{c.title}</div>
                    <div className="text-xs text-neutral-500">{c.duration} · {c.fees}</div>
                  </div>
                </label>
              ))}
              {courses.length === 0 && <div className="text-sm text-neutral-500 col-span-2">No published courses yet — add courses in the Course Manager.</div>}
            </div>
          </div>
        </div>)}

        {tab === "partners" && (<div>
          <p className="text-sm text-neutral-500 mb-4">Hiring partner logos shown on the About + Home pages.</p>
          <div className="mb-6 bg-sky-50 rounded-xl p-4 border border-sky-100">
            <F label="Auto-count hiring partners (uses number of visible partners below)" path="about_page.hiring_partners_auto_count" type="checkbox" />
            <F label="Manual count value (used when auto-count is OFF, e.g. 50+)" path="about_page.hiring_partners_manual_count" />
          </div>
          <div className="space-y-2">
            {partners.map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-xl border border-neutral-200" data-testid={`partner-row-${i}`}>
                <input value={p.name || ""} onChange={(e) => setPartner(i, "name", e.target.value)} placeholder="Name" className="w-40 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`partner-name-${i}`} />
                <input value={p.logo || ""} onChange={(e) => setPartner(i, "logo", e.target.value)} placeholder="Logo URL" className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-xs" />
                <input value={p.website || ""} onChange={(e) => setPartner(i, "website", e.target.value)} placeholder="Website URL" className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-xs" />
                <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={p.featured} onChange={(e) => setPartner(i, "featured", e.target.checked)} /> ★</label>
                <button onClick={() => setPartner(i, "visible", !(p.visible !== false))} data-testid={`partner-visible-${i}`} className={`p-1.5 rounded ${p.visible !== false ? "bg-sky-100 text-sky-700" : "bg-neutral-100"}`}>
                  {p.visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => rmPartner(i)} className="p-1.5 hover:bg-red-100 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={addPartner} className="mt-4 btn-outline-eq text-sm" data-testid="partner-add"><Plus className="w-4 h-4" /> Add partner</button>
        </div>)}

        {tab === "tools" && (<div className="grid gap-4">
          <F label="Tools section heading" path="about_page.tools_heading" />
          {tools.map((k) => (
            <div key={k}>
              <label className="text-xs font-semibold text-neutral-600">{k.replace("tools_", "").toUpperCase()} tools (comma-separated)</label>
              <input value={(ap[k] || []).join(", ")} onChange={(e) => set(`about_page.${k}`, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                     className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`about-${k}`} />
            </div>
          ))}
        </div>)}

        {tab === "founder" && (<div className="grid gap-4">
          <F label="Founder heading" path="about_page.founder_heading" />
          <F label="Founder name" path="about_page.founder_name" />
          <F label="Founder role" path="about_page.founder_role" />
          <F label="Founder image URL" path="about_page.founder_image" />
          <F label="Founder message" path="about_page.founder_message" type="textarea" rows={4} />
          <F label="Video section heading" path="about_page.video_heading" />
          <F label="YouTube video URL" path="about_page.video_youtube_url" />
        </div>)}

        {tab === "cta" && (<div className="grid gap-4">
          <F label="CTA heading" path="about_page.cta_heading" />
          <F label="CTA button text" path="about_page.cta_button_text" />
          <F label="CTA button link (URL or /path)" path="about_page.cta_button_link" />
        </div>)}

        {tab === "seo" && (<div className="grid gap-4">
          <F label="Meta title" path="about_page.seo_meta_title" />
          <F label="Meta description" path="about_page.seo_meta_description" type="textarea" />
          <F label="Keywords" path="about_page.seo_keywords" />
          <F label="Open Graph image URL" path="about_page.seo_og_image" />
          <F label="No-index this page" path="about_page.seo_noindex" type="checkbox" />
        </div>)}
      </div>
    </div>
  );
}
